import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { GetProjectResponse } from "api/generatedApi";
import { useTranslation } from "react-i18next";
import styled from "@mui/system/styled";
import { costumPalette } from "theme/palette";
import { formatDate, formatNumberToAmount, formatTimestamp, getDateFromDateOnly } from "utils/formats";

interface Props {
  project: GetProjectResponse;
  usePrintLayout: boolean;
}

export const ProjectTypeAndInfoContent = ({ project, usePrintLayout }: Props) => {
  const { t } = useTranslation();

  const GridBoxTopStyled = styled(Box)`
    height: ${usePrintLayout ? "auto" : "20%"};
    width: 100%;
  `;

  const GridBoxBottomStyled = styled(Box)`
    height: ${usePrintLayout ? "auto" : "70%"};
    width: 100%;
    border-top: 1px solid ${usePrintLayout ? "transparent" : costumPalette.gray};
    padding-left: ${usePrintLayout ? 0 : "30px"};
    padding-right: ${usePrintLayout ? 0 : "30px"};
  `;

  return (
    <Grid>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, px: usePrintLayout ? 0 : "30px", pb: 0 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          {/* title */}
          <Box sx={{ wordWrap: "break-word", width: "49%" }}>
            <Typography variant={"overline"} color={"primary.main"}>
              {t("dashboard.projectInfo.projectTypeAndInfo.project")}
            </Typography>
            <Typography variant={"body2"} color={"primary.main"}>
              {project.title}
            </Typography>
          </Box>
          {/* piecework type */}
          <Box sx={{ wordWrap: "break-word", width: "49%" }}>
            <Typography variant={"overline"} color={"primary.main"}>
              {t("dashboard.projectInfo.projectTypeAndInfo.type")}
            </Typography>
            <Typography variant={"body2"} color={"primary.main"}>
              {t("project.pieceworkTypes." + project.pieceworkType + ".short")}
            </Typography>
          </Box>
        </Box>
        {/* piecework number */}
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box sx={{ width: "49%", wordWrap: "break-word" }}>
            <Typography variant={"overline"} color={"primary.main"}>
              {t("dashboard.projectInfo.projectTypeAndInfo.pieceworkNumber")}
            </Typography>
            <Typography variant={"body2"} color={"primary.main"}>
              {project.pieceWorkNumber}
            </Typography>
          </Box>
          {/* order number */}
          <Box sx={{ width: "49%", wordWrap: "break-word" }}>
            <Typography variant={"overline"} color={"primary.main"}>
              {t("dashboard.projectInfo.projectTypeAndInfo.orderNumber")}
            </Typography>
            <Typography variant={"body2"} color={"primary.main"}>
              {project.orderNumber}
            </Typography>
          </Box>
        </Box>
        <Box></Box>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplate: "1fr 1fr",
          flexGrow: 1,
        }}
      ></Box>
      <GridBoxTopStyled>
        <Box sx={{ px: usePrintLayout ? 0 : "30px", display: "flex", flexDirection: "column", gap: 1, height: "auto" }}>
          {project.pieceworkType === "TwelveTwo" && (
            <Box>
              <Typography variant={"overline"} color={"primary.main"}>
                {t("dashboard.projectInfo.projectTypeAndInfo.sum")}
              </Typography>
              <Typography variant={"body2"} color={"primary.main"}>
                {`${formatNumberToAmount(project.lumpSumPaymentDkr ?? 0, 2)} ${t("common.currency")}`}
              </Typography>
            </Box>
          )}
        </Box>
      </GridBoxTopStyled>
      {/*start and end date*/}
      <GridBoxBottomStyled>
        <Box sx={{}}>
          <Typography variant={"overline"} color={"primary.main"}>
            {t("dashboard.projectInfo.projectTypeAndInfo.period")}
          </Typography>
          <Typography variant={"body2"} color={"primary.main"}>
            {formatDate(getDateFromDateOnly(project.startDate ?? undefined, "dd-mm-yyyy"))} - {formatDate(getDateFromDateOnly(project.endDate ?? undefined, "dd-mm-yyyy"))}
          </Typography>
        </Box>

        {/* project number and date */}

        <Box sx={{ pt: 0, display: "flex", flexDirection: "row", gap: 2, height: "35%" }}>
          <Box sx={{ width: "30%" }}>
            <Typography variant={"overline"} color={"primary.main"}>
              {t("dashboard.projectInfo.projectTypeAndInfo.projectNumber")}
            </Typography>
            <Typography variant={"body2"} color={"primary.main"}>
              {project.projectNumber}
            </Typography>
          </Box>
          <Box sx={{ width: "65%" }}>
            <Typography variant={"overline"} color={"primary.main"}>
              {t("dashboard.projectInfo.projectTypeAndInfo.projectDate")}
            </Typography>
            <Typography variant={"body2"} color={"primary.main"}>
              {formatTimestamp(project.projectCreatedTime)}
            </Typography>
          </Box>
        </Box>
      </GridBoxBottomStyled>
    </Grid>
  );
};
